import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Phone from './Phone';
import BottomNav from './BottomNav';
import Icon from './Icon';
import { C } from './theme';
import { useAuth } from '../context/AuthContext';
import UserProfileDropdown, { getInitials } from './layout/UserProfileDropdown';

// Category label -> route path for report screens that exist
const CATEGORY_PATHS = { 'Sales': '/reports/sales', 'Product': '/reports/product', 'Offers': '/reports/offers', 'EOD Summary': '/reports/eod', 'Labor': '/reports/labor', 'Loyalty': '/reports/loyalty', 'Check-in': '/reports/checkin', 'Reservation': '/reports/reservation', 'Customer': '/reports/customers', 'Event': '/reports/events', 'Voice Agent': '/reports/voice-agent' };

// UI only — matches mobile-v6-1.jsx Reports screen (no API integration)
const categories = [
    { icon: 'bar_chart', label: 'Sales', desc: 'Revenue, orders, channels & hourly trends', color: C.accent, bg: C.accentSoft },
    { icon: 'restaurant_menu', label: 'Product', desc: 'Item & category performance, discounts', color: C.green, bg: C.greenSoft },
    { icon: 'local_offer', label: 'Offers', desc: 'Redemptions, discounts & top offers', color: C.accent, bg: C.accentSoft },
    { icon: 'receipt_long', label: 'EOD Summary', desc: 'End-of-day reconciliation & funding', color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
    { icon: 'schedule', label: 'Labor', desc: 'Staff hours, SPLH, tips & void tracking', color: '#0D9488', bg: 'rgba(13,148,136,0.08)' },
    { icon: 'loyalty', label: 'Loyalty', desc: 'Members, points liability & redemption', color: C.accent, bg: C.accentSoft },
    { icon: 'how_to_reg', label: 'Check-in', desc: 'Guest arrivals, wait times & party sizes', color: '#0891B2', bg: 'rgba(8,145,178,0.08)' },
    { icon: 'event_available', label: 'Reservation', desc: 'Booking trends, covers & no-show rates', color: C.orange, bg: C.orangeSoft },
    { icon: 'person', label: 'Customer', desc: 'Guest profiles, frequency & lifetime value', color: C.green, bg: C.greenSoft },
    { icon: 'celebration', label: 'Event', desc: 'Pre-orders, pickup slots & event analytics', color: C.red, bg: C.redSoft },
    // { icon: 'chat_bubble', label: 'Guest Review', desc: 'Ratings, sentiment & review replies', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', superOnly: true },
    { icon: 'support_agent', label: 'Voice Agent', desc: 'AI call volume, conversion & quality', color: C.accent, bg: C.accentSoft },
];

const ReportsScreen = () => {
    const navigate = useNavigate();
    const { auth, logout } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const displayName = auth?.fullName ?? auth?.userId ?? auth?.userName ?? 'User';
    const userRole = auth?.role ?? auth?.designation ?? 'Restaurant_Manager';
    const user = {
        initials: getInitials(displayName),
        name: displayName,
        role: userRole,
        onLogout: () => logout(),
    };

    const filteredCategories = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return categories;
        return categories.filter((cat) => cat.label.toLowerCase().includes(q));
    }, [searchQuery]);

    return (
    <Phone>
        <div className="reports-screen-header" style={{ padding: '8px 20px 12px', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.t1 }}>Reports</div>
                <UserProfileDropdown user={user} iconOnly />
            </div>
            <div style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>Select a category to explore</div>
            <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 10, background: C.hover, border: `1px solid ${C.cardBorder}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="search" size={16} color={C.t3} />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search reports..."
                    style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        fontSize: 12,
                        color: C.t1,
                        outline: 'none',
                    }}
                />
            </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 16px' }}>
            {filteredCategories.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, fontSize: 13, color: C.t3 }}>
                    No reports match "{searchQuery.trim()}"
                </div>
            ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {filteredCategories.map((cat, i) => {
                    const isLast = i === filteredCategories.length - 1;
                    const isOdd = filteredCategories.length % 2 !== 0;
                    return (
                        <div
                            key={i}
                            onClick={() => { const path = CATEGORY_PATHS[cat.label]; if (path) navigate(path); }}
                            role={CATEGORY_PATHS[cat.label] ? 'button' : undefined}
                            style={{
                                gridColumn: isLast && isOdd ? '1 / -1' : undefined,
                                background: C.card,
                                border: `1px solid ${C.cardBorder}`,
                                borderRadius: 14,
                                padding: '16px 14px',
                                cursor: CATEGORY_PATHS[cat.label] ? 'pointer' : 'pointer',
                                boxShadow: C.shadow,
                                position: 'relative',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                            }}
                        >
                            <div style={{ position: 'absolute', top: -16, right: -16, width: 56, height: 56, borderRadius: '50%', background: cat.bg, filter: 'blur(14px)', pointerEvents: 'none' }} />
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: cat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon name={cat.icon} size={20} color={cat.color} />
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, lineHeight: 1.2 }}>{cat.label}</div>
                            <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.45 }}>{cat.desc}</div>
                            <div style={{ position: 'absolute', bottom: 10, right: 10, width: 18, height: 18, borderRadius: 6, background: cat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon name="chevron_right" size={14} color={cat.color} />
                            </div>
                        </div>
                    );
                })}
            </div>
            )}
        </div>
        <BottomNav />
    </Phone>
    );
};

export default ReportsScreen;

